import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getStrategyActionConfig,
  STRATEGY_ACTION_SIZES,
} from '../../design-system/strategyActions';

import './StrategyActionButton.css';

/**
 * Shared button component for strategy actions (deploy, backtest, edit, share).
 * Centralises icons, colours, and typography so updates cascade globally.
 */
const StrategyActionButton = forwardRef(function StrategyActionButton(
  {
    actionType,
    as: Component = 'button',
    label,
    icon,
    size = 'medium',
    fullWidth = false,
    className,
    disabled = false,
    isLoading = false,
    loadingLabel,
    style,
    children,
    ...rest
  },
  ref,
) {
  const actionConfig = useMemo(
    () => getStrategyActionConfig(actionType),
    [actionType],
  );

  if (!actionConfig) {
    console.warn(
      `[StrategyActionButton] Unknown actionType "${actionType}". Falling back to neutral styling.`,
    );
  }

  const {
    label: defaultLabel,
    icon: defaultIcon,
    colors = {},
  } = actionConfig || {};

  const resolvedLabel = loadingLabel && isLoading
    ? loadingLabel
    : label || defaultLabel || children;

  const resolvedIcon = isLoading
    ? null
    : icon || defaultIcon || null;

  const sizeVars = STRATEGY_ACTION_SIZES[size] || {};

  const visualVars = {
    '--sa-background': colors.background,
    '--sa-background-hover': colors.backgroundHover,
    '--sa-border': colors.border,
    '--sa-border-hover': colors.borderHover,
    '--sa-box-shadow': colors.boxShadow,
    '--sa-box-shadow-hover': colors.boxShadowHover,
    '--sa-text-color': colors.textColor || '#ffffff',
  };

  const mergedStyle = {
    ...sizeVars,
    ...visualVars,
    ...style,
  };

  const isButtonElement = Component === 'button';
  const isDisabled = disabled || isLoading;

  const componentProps = {
    ref,
    className: clsx(
      'sa-button',
      `sa-button--size-${size in STRATEGY_ACTION_SIZES ? size : 'medium'}`,
      {
        'sa-button--full-width': fullWidth,
        'sa-button--loading': isLoading,
        'sa-button--disabled': isDisabled,
      },
      className,
    ),
    style: mergedStyle,
    ...rest,
  };

  if (isButtonElement) {
    componentProps.type = rest.type || 'button';
    componentProps.disabled = isDisabled;
  } else if (isDisabled) {
    componentProps['aria-disabled'] = true;
    componentProps.tabIndex = -1;
    componentProps.onClick = (event) => {
      event.preventDefault();
    };
  }

  return (
    <Component {...componentProps}>
      {isLoading ? (
        <span className="sa-button__spinner" aria-hidden="true" />
      ) : resolvedIcon ? (
        <FontAwesomeIcon icon={resolvedIcon} className="sa-button__icon" />
      ) : null}
      {resolvedLabel ? (
        <span className="sa-button__label">{resolvedLabel}</span>
      ) : null}
      {children}
    </Component>
  );
});

StrategyActionButton.propTypes = {
  actionType: PropTypes.oneOf([
    'startTrading',
    'runBacktest',
    'editStrategy',
    'shareStrategy',
  ]).isRequired,
  as: PropTypes.elementType,
  label: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  size: PropTypes.oneOf(['hero', 'large', 'medium', 'compact', 'mini']),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};

export default StrategyActionButton;
